/**
 * Reads of the creatives service that every reader in the hub shares for a while: the Website hub's cards and the
 * Games tab ask for the same page and list states, and one reader leaving (a card unmounting, a page switch) must
 * never cancel a read another reader waits on.
 *
 * - `get` answers from an answer younger than the keep time, else joins the read in flight, else starts `read()`. The
 *   read carries no reader's signal: a reader checks its own abort once it has the answer.
 * - Only an answer that came in (`ok`) is kept. After a failed or unavailable read, the next reader reads again.
 * - A write drops what it changes (`drop`), before it is sent and again once it settles. A read in flight at a drop
 *   still answers its own reader but is not kept, and a reader after the drop does not join it: each key has a
 *   generation, which a drop bumps, and an answer is kept only under the generation its read started in.
 */
import type { TenantSourceResult } from './creatives-tenant-source';

/** How long a shared answer stands. */
export const SHARED_READ_MS = 60_000;

export interface SharedReads<T> {
  /** The answer for `key` while it is younger than the keep time, else undefined: a reader can open with it. */
  peek: (key: string) => T | undefined;
  /** The answer for `key`, else the read in flight, else `read()` (see above). */
  get: (key: string, read: () => Promise<TenantSourceResult<T>>) => Promise<TenantSourceResult<T>>;
  /** Forget the answers and the reads in flight of every key `match` accepts. */
  drop: (match: (key: string) => boolean) => void;
}

/** A store of shared reads, each answer kept `keepMs`. */
export const sharedReads = <T>(keepMs: number = SHARED_READ_MS): SharedReads<T> => {
  const answers = new Map<string, { value: T; at: number }>();
  const asking = new Map<string, Promise<TenantSourceResult<T>>>();
  const generations = new Map<string, number>();
  const generationOf = (key: string): number => generations.get(key) ?? 0;
  const fresh = (key: string): { value: T } | undefined => {
    const kept = answers.get(key);
    return kept && Date.now() - kept.at < keepMs ? kept : undefined;
  };

  return {
    peek: (key) => fresh(key)?.value,
    get: (key, read) => {
      const kept = fresh(key);
      if (kept) return Promise.resolve<TenantSourceResult<T>>({ ok: true, value: kept.value });
      const inFlight = asking.get(key);
      if (inFlight) return inFlight;
      const generation = generationOf(key);
      const request = read()
        .then((result) => {
          if (result.ok && generationOf(key) === generation) answers.set(key, { value: result.value, at: Date.now() });
          return result;
        })
        .finally(() => {
          // After a drop the key may belong to a newer read: only this read's own entry is cleared.
          if (asking.get(key) === request) asking.delete(key);
        });
      asking.set(key, request);
      return request;
    },
    drop: (match) => {
      for (const key of new Set([...answers.keys(), ...asking.keys()])) {
        if (!match(key)) continue;
        answers.delete(key);
        asking.delete(key);
        generations.set(key, generationOf(key) + 1);
      }
    },
  };
};
